import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, Row, Col, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { InfoCircleOutlined } from "@ant-design/icons";
import styles from "./ExperienceBooking.module.css"

const { Text } = Typography;

const GuestsSelector = ({ counts, setCounts, activeTab, error, setBookingErrors }) => {
    const [t] = useTranslation()
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const updateCount = (key, delta) => {

        const newCounts = {
            ...counts,
            [key]: Math.max(0, counts[key] + delta),
        };

        setCounts(newCounts);

        const newTotal =
            newCounts?.adults + newCounts?.children + newCounts?.infants + newCounts?.passengers;
        if (newTotal > 0 && setBookingErrors) {
            setBookingErrors(prev => ({ ...prev, counts: false }));
        }
    };

    const clearCounts = () => {
        setCounts({ adults: 0, children: 0, infants: 0, passengers: 0 });
    };

    const totalGuests =
        counts?.adults + counts?.children + counts?.infants + counts?.passengers;

    const getGuestsSummary = () => {
        const labels = {
            adults: "Adult",
            children: "Child",
            infants: "Infant",
            passengers: "Passenger",
        };

        return Object.entries(counts)
            .filter(([_, count]) => count > 0)
            .map(([key, count]) => `${count} ${labels[key]}${count > 1 ? "s" : ""}`)
            .join(", ");
    };

    return (
        <div className={styles.guestsWrapper} ref={containerRef}>
            <div className={`${styles.inputContainer} ${open ? styles.activeBorder : ""} ${error ? styles.errorBorder : ""}`}>
                <span className={styles.inputLabel}>Guests</span>
                <button className={styles.inputLink} onClick={() => {
                    if (activeTab !== 2 && activeTab !== 3) {
                        setOpen(!open)
                    }
                }}>
                    {totalGuests > 0 ? getGuestsSummary() : "Add Guests"}
                </button>
            </div>
            {error && <Text style={{ color: "red" }}>{t("webIntegration.embedded_validation.guest")}</Text>}
            {open && (
                <div className={styles.dropdownPanel}>
                    {[
                        { label: "Adults", key: "adults" },
                        { label: "Children (< 12 years)", key: "children" },
                        { label: "Infants (< 2 years)", key: "infants" },
                        {
                            label: (
                                <>
                                    Passengers{" "}
                                    <InfoCircleOutlined style={{ fontSize: 12, color: "#1890ff" }} />
                                </>
                            ),
                            key: "passengers",
                        },
                    ].map(({ label, key }) => (
                        <Row key={key} justify="space-between" align="middle" style={{ marginBottom: 10 }}>
                            <Col>{label}</Col>
                            <Col>
                                <Button size="middle" onClick={(e) => { e.stopPropagation(); updateCount(key, -1); }}>-</Button>
                                <Text style={{ margin: "0 12px" }}>{counts[key]}</Text>
                                <Button size="middle" onClick={(e) => { e.stopPropagation(); updateCount(key, 1); }}>+</Button>
                            </Col>
                        </Row>
                    ))}
                    <Button type="link" onClick={(e) => { e.stopPropagation(); clearCounts(); }}>
                        Clear
                    </Button>
                </div>
            )}
        </div>
    );
};

GuestsSelector.propTypes = {
    counts: PropTypes.shape({
        adults: PropTypes.number,
        children: PropTypes.number,
        infants: PropTypes.number,
        passengers: PropTypes.number,
    }).isRequired,
    setCounts: PropTypes.func.isRequired,
    activeTab: PropTypes.number,
    error: PropTypes.bool,
    setBookingErrors: PropTypes.func,
};

export default React.memo(GuestsSelector);
